import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import InputT from '@components/tailwind-components/Input/InputT';
import RadioInputT from '@components/tailwind-components/Input/RadioInputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import {
  reportIssueCategoryMapping,
  reportIssuePriorityMapping,
  type TReportIssueParams,
} from '@conversed/rest/src/contracts/llm-usages/report-issue';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { iconsHelper } from '../utils/iconsHelper';
import FactCheckingAttachmentSection from './FactCheckingAttachmentSection';

export interface Props {
  reportIssueParams: TReportIssueParams;
  setReportIssueParams: (params: TReportIssueParams) => void;
}

export const FactCheckingReportIssueSection: React.FC<Props> = ({
  reportIssueParams,
  setReportIssueParams,
}) => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-x-0 gap-y-1 pb-2 w-full'>
      <div>
        <LabelT
          text={t('train-ai.fact-checking.report-issue.category.label')}
          className='text-lg font-medium text-red-base mb-1.5'
        />
        {reportIssueCategoryMapping.map(
          ({ key, labelTranslationKey, icon }) => (
            <CheckboxInputT
              key={key}
              id={key}
              label={t(labelTranslationKey)}
              imgSrc={iconsHelper[icon]}
              className='!my-0'
              labelClassName='text-xs text-primary-base'
              checked={reportIssueParams.categories.includes(key)}
              onChange={() => {
                const newCategories = reportIssueParams.categories.includes(key)
                  ? reportIssueParams.categories.filter((cat) => cat !== key)
                  : [...reportIssueParams.categories, key];

                setReportIssueParams({
                  ...reportIssueParams,
                  categories: newCategories,
                });
              }}
            />
          ),
        )}
      </div>

      <div className='tailwind-divider-x w-full border-red-base opacity-20' />

      <div>
        <LabelT
          text={t('train-ai.fact-checking.report-issue.priority.label')}
          className='text-lg font-medium text-red-base mb-1.5'
        />
        {reportIssuePriorityMapping.map(
          ({ key, labelTranslationKey, icon }) => (
            <RadioInputT
              key={key}
              id={key}
              name='priority'
              label={t(labelTranslationKey)}
              imgSrc={iconsHelper[icon]}
              className='!my-0'
              labelClassName='text-xs text-primary-base'
              checked={reportIssueParams.priority === key}
              onChange={() =>
                setReportIssueParams({
                  ...reportIssueParams,
                  priority: key,
                })
              }
            />
          ),
        )}
      </div>

      <div className='tailwind-divider-x w-full border-red-base opacity-20' />

      <div>
        <LabelT
          text={t('train-ai.fact-checking.report-issue.comment.label')}
          className='text-lg font-medium text-red-base mb-1.5'
        />
        <InputT
          id='comment'
          type='text'
          className='!my-0'
          placeholder={t(
            'train-ai.fact-checking.report-issue.comment.placeholder',
          )}
          options={{
            value: reportIssueParams.comment,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              setReportIssueParams({
                ...reportIssueParams,
                comment: e.target.value,
              });
            },
          }}
        />
      </div>
      <div className='tailwind-divider-x w-full border-red-base opacity-20' />
      <FactCheckingAttachmentSection
        reportIssueParams={reportIssueParams}
        setReportIssueParams={(params) => setReportIssueParams(params)}
      />
    </div>
  );
};

export default FactCheckingReportIssueSection;
